import { BadgePlusIcon, ChevronDownIcon, CircleCheckIcon, GiftIcon } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import { Progress } from '@/shared/components/ui/progress';
import { Sale, SaleBonus, SaleBonusSource } from '@/types/api/sale';
import { cn } from '@/utils/shared/Utils';
import { bonusValueText, formatKronor, thresholdProgress, thresholdShortText } from '@/utils/sale/bonus';

interface SaleProfitBreakdownProps {
  sale: Sale;
}

/** Dropdown with how the group's profit adds up, and the bonuses the sale has or can unlock. */
export function SaleProfitBreakdown({ sale }: SaleProfitBreakdownProps) {
  const currency = sale.profit.currency;
  // What the rate bonus adds, derived from the totals so the rows add up to the profit.
  const rateBonus = Math.max(
    0,
    sale.profit.amount + sale.cappedAmount.amount - sale.baseProfit.amount - sale.bonusAmount.amount,
  );
  const bonuses = sale.bonuses.filter((b) => !b.revokedAt);
  // Tipsbelöningar are fixed bonuses too, but get their own row.
  const commission = bonuses
    .filter((b) => b.source === SaleBonusSource.Commission && b.applied)
    .reduce((sum, b) => sum + (b.amount?.amount ?? 0), 0);
  const fixedBonus = sale.bonusAmount.amount - commission;

  return (
    <Collapsible>
      <CollapsibleTrigger className="group flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100">
        Så räknas vinsten
        <ChevronDownIcon
          size={16}
          className="transition-transform group-data-[state=open]:rotate-180"
          aria-hidden="true"
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-3 rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
        <dl className="space-y-2 text-sm">
          <Row label="Försäljning" value={formatKronor(sale.revenue.amount, currency)} muted />
          <Row label={`Grundvinst ${sale.baseProfitRate} %`} value={formatKronor(sale.baseProfit.amount, currency)} />
          {rateBonus > 0 && (
            <Row label={`Bonus +${sale.bonusRate} %`} value={`+${formatKronor(rateBonus, currency)}`} bonus />
          )}
          {fixedBonus > 0 && <Row label="Bonus" value={`+${formatKronor(fixedBonus, currency)}`} bonus />}
          {commission > 0 && <Row label="Tipsbelöning" value={`+${formatKronor(commission, currency)}`} bonus />}
          {sale.cappedAmount.amount > 0 && (
            <Row
              label="Begränsat till försäljningen"
              value={`−${formatKronor(sale.cappedAmount.amount, currency)}`}
              muted
            />
          )}
          <div className="flex items-center justify-between gap-4 border-t border-gray-100 pt-2 text-base font-semibold text-gray-800 dark:border-gray-700/60 dark:text-gray-100">
            <dt>Er vinst</dt>
            <dd className="tabular-nums">{formatKronor(sale.profit.amount, currency)}</dd>
          </div>
        </dl>

        {bonuses.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500">
              Bonusar{bonuses.length > 2 && ` (${bonuses.length})`}
            </h3>
            {/* About two and a half tiles, so a cut-off tile hints that the list scrolls. */}
            <div className="-me-2 max-h-64 space-y-2 overflow-y-auto overscroll-contain pe-2">
              {bonuses.map((bonus) => (
                <BonusRow key={bonus.id} bonus={bonus} sale={sale} />
              ))}
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function Row({ label, value, muted, bonus }: { label: string; value: string; muted?: boolean; bonus?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4',
        muted && 'text-gray-500 dark:text-gray-400',
        bonus && 'text-green-700 dark:text-green-400',
      )}
    >
      <dt className="flex items-center gap-1.5">
        {bonus && <BadgePlusIcon size={14} aria-hidden="true" />}
        {label}
      </dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

function BonusRow({ bonus, sale }: { bonus: SaleBonus; sale: Sale }) {
  const isCommission = bonus.source === SaleBonusSource.Commission;
  const source = isCommission
    ? 'Från ett tips'
    : bonus.source === SaleBonusSource.Code && sale.bonusCode
      ? `${sale.bonusCode.startsWith('TIPSA-') ? 'Tipskod' : 'Bonuskod'} ${sale.bonusCode}`
      : 'Bonus från Piggo';
  const progress = !bonus.applied && bonus.threshold ? thresholdProgress(bonus.threshold, sale) : null;

  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2.5',
        bonus.applied
          ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10'
          : 'border-gray-200 bg-white dark:border-gray-700/60 dark:bg-gray-800',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
            {isCommission && <GiftIcon size={14} className="text-violet-500 dark:text-violet-400" aria-hidden="true" />}
            {isCommission ? `Tipsbelöning ${formatKronor(bonus.amount?.amount ?? 0)}` : bonusValueText(bonus)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{source}</div>
        </div>
        {bonus.applied && (
          <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
            <CircleCheckIcon size={14} aria-hidden="true" />
            Upplåst
          </span>
        )}
      </div>

      {progress && (
        <div className="mt-2 space-y-1">
          <Progress value={progress.percent} className="h-1.5 bg-gray-100 dark:bg-gray-700/60" />
          <div className="flex justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Låses upp {thresholdShortText(bonus.threshold!)}</span>
            <span className="shrink-0 tabular-nums">{progress.leftText}</span>
          </div>
        </div>
      )}
    </div>
  );
}
